"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useMemo } from "react";

import { singIn } from "@/features/auth/api/login";
import useAuthStore from "@/features/auth/store/useAuthStore";
import errorHandler, { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CardDescription } from "@/components/ui/card.tsx";

interface EmailFormProps {
  goToNextStep: () => void;
}

const EmailForm = ({ goToNextStep }: EmailFormProps) => {
  const { t } = useTranslation(["forms", "common", "auth"]);
  const createEmailFormSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, { message: t("forms:errors.invalidEmail") })
          .email({ message: t("forms:errors.invalidEmail") })
          .regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
            message: t("forms:errors.invalidEmail"),
          })
          .trim(),
      }),
    [i18n.language],
  );

  type EmailFormValues = z.infer<typeof createEmailFormSchema>;

  const setEmail = useAuthStore((state) => state.setEmail);

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(createEmailFormSchema),
    mode: "onTouched",
    defaultValues: {
      email: "",
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    control,
  } = form;

  const onSubmit: SubmitHandler<EmailFormValues> = async ({ email }) => {
    try {
      const res = await singIn(email);

      console.log(res);
      setEmail(email);
      goToNextStep();
    } catch (error: any) {
      const apiErrors = errorHandler<"email">(error);

      if (error.response.data.code) {
        form.setError("email", { message: t("auth:error.emailNotFound") });
      }

      assignApiErrorsToFormFields(apiErrors, setError, { email: "email" });
    }
  };

  return (
    <Form {...form}>
      <motion.form
        animate={{ opacity: 1, y: 0 }}
        className="grid gap-6 md:p-10 md:pt-4"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <CardDescription className="text-muted-foreground m-auto line-clamp-2 block max-w-80 text-center md:w-3/4">
          {t("auth:signIn.weWillSignYouIn")}
        </CardDescription>
        <motion.div
          animate={{ opacity: 1 }}
          className="grid gap-4"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <motion.div className="relative" whileTap={{ scale: 0.98 }}>
                    <span className="iconify tabler--mail-filled absolute left-2 h-full" />
                    <Input
                      className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 py-0 pl-8 text-sm transition-all duration-200 md:py-6 ${
                        errors.email
                          ? "border-destructive focus-visible:border-secondary focus-visible:ring-destructive"
                          : ""
                      }`}
                      disabled={isSubmitting}
                      placeholder={t("forms:labels.yourEmail")}
                      {...field}
                    />
                  </motion.div>
                </FormControl>
                <AnimatePresence>
                  {errors.email && (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FormMessage />
                    </motion.div>
                  )}
                </AnimatePresence>
              </FormItem>
            )}
          />
        </motion.div>
        <motion.div
          animate={{ opacity: 1 }}
          className="grid gap-4"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background font-medium shadow-xl"
            color="primary"
            disabled={isSubmitting || !form.formState.isValid}
            size="lg"
            type="submit"
          >
            {isSubmitting ? (
              <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
                {t("common:submitting")}
              </motion.div>
            ) : (
              <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
                {t("common:continue")}
              </motion.div>
            )}
          </Button>
        </motion.div>
      </motion.form>
    </Form>
  );
};

export default EmailForm;
